import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ObjectDetailDto, ReviewDto } from '../api/models';
import { BrandingService } from './branding.service';

export interface SeoData {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
}

const SITE_NAME = 'Zaklepuj.pl';
const DEFAULT_DESCRIPTION = 'Znajdź idealny nocleg w Polsce. Domki, apartamenty, pokoje – porównaj ceny i zarezerwuj online.';
const DEFAULT_IMAGE = 'https://zaklepuj.pl/assets/images/og-default.png';
const CANONICAL_BASE = 'https://zaklepuj.pl';

/** Placeholders accepted inside a white-label metaTitle template. */
const TITLE_PLACEHOLDER = /\{(?:name|title)\}/;

/** Maximum number of individual review nodes embedded in JSON-LD. */
const MAX_REVIEW_NODES = 10;

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly doc = inject(DOCUMENT);
  private readonly branding = inject(BrandingService);

  /** Site name to display — the branded display name on a white-label host. */
  private siteName(): string {
    const brand = this.branding.displayName();
    return this.branding.isWhiteLabel() && brand ? brand : SITE_NAME;
  }

  /**
   * Set meta tags for a page. Call on each route change.
   */
  update(data: SeoData): void {
    const site = this.siteName();
    const fullTitle = data.title === site
      ? data.title
      : `${data.title} | ${site}`;

    this.title.setTitle(fullTitle);

    // Basic meta
    this.setTag('description', data.description);

    // Open Graph
    this.setTag('og:title', data.title);
    this.setTag('og:description', data.description);
    this.setTag('og:image', data.image ?? DEFAULT_IMAGE);
    this.setTag('og:url', data.url ?? '');
    this.setTag('og:type', data.type ?? 'website');
    this.setTag('og:site_name', site);
    this.setTag('og:locale', this.branding.defaultLang() === 'en' ? 'en_US' : 'pl_PL');

    // Twitter Card
    this.setTag('twitter:card', data.image ? 'summary_large_image' : 'summary');
    this.setTag('twitter:title', data.title);
    this.setTag('twitter:description', data.description);
    this.setTag('twitter:image', data.image ?? DEFAULT_IMAGE);
  }

  /**
   * Set (or remove, with `null`) the `<meta name="robots">` directive — e.g.
   * `'noindex, nofollow'` for pages that must not be indexed (drafts, expired
   * request-board posts). Pages that set it must clear it on destroy.
   */
  setRobots(value: string | null): void {
    if (value) {
      this.meta.updateTag({ name: 'robots', content: value });
    } else {
      this.meta.removeTag("name='robots'");
    }
  }

  /**
   * Create or update the single `<link rel="canonical">` in `<head>`; `null`
   * (or an empty string) removes it — e.g. pages without an own canonical must
   * not keep the one left by the previous SPA route. `update()` does not touch
   * the canonical: pages set it explicitly (contract §15.1 — a request's
   * canonical is its URL without `?t=`).
   */
  setCanonical(url: string | null): void {
    const head = this.doc.head;
    if (!head) {
      return;
    }
    const links = Array.from(head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
    if (!url) {
      links.forEach((existing) => existing.remove());
      return;
    }
    let link = links.shift();
    // Exactly one canonical — drop duplicates (e.g. a static one from index.html).
    links.forEach((duplicate) => duplicate.remove());
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /**
   * Set default meta tags (for homepage / listing page).
   */
  setDefaults(): void {
    this.update({
      title: `${SITE_NAME} – Znajdź idealny nocleg`,
      description: DEFAULT_DESCRIPTION,
      image: DEFAULT_IMAGE,
      url: CANONICAL_BASE,
    });
    this.setCanonical(CANONICAL_BASE);
  }

  /**
   * Set meta tags for the white-label portfolio landing page. Title and
   * description derive from the owner's display name (a configured metaTitle /
   * metaDescription still wins). The canonical/og:url follow the same policy as
   * property pages: the host's own origin when canonicalMode === 'self',
   * otherwise the zaklepuj.pl base so subdomains don't duplicate content.
   */
  setPortfolio(): void {
    const name = this.branding.displayName() ?? SITE_NAME;
    const title = this.branding.metaTitle()?.trim() || name;
    const description =
      this.branding.metaDescription()?.trim() || DEFAULT_DESCRIPTION;
    const image = this.branding.ogImageUrl() ?? DEFAULT_IMAGE;
    const url = this.portfolioUrl();

    this.update({
      title,
      description,
      image,
      url,
      type: 'website',
    });

    this.setCanonical(url);
  }

  /**
   * Build the canonical / og:url for the portfolio landing page. Mirrors
   * propertyUrl: the host's own origin (root path) when canonicalMode === 'self',
   * otherwise the zaklepuj.pl base.
   */
  private portfolioUrl(): string {
    if (this.branding.isWhiteLabel() && this.branding.canonicalMode() === 'self') {
      const origin = this.currentOrigin();
      if (origin) {
        return origin;
      }
    }
    return CANONICAL_BASE;
  }

  /**
   * Set meta tags for a property detail page.
   */
  setProperty(property: ObjectDetailDto): void {
    const city = property.address?.city ?? null;
    const baseTitle = city ? `${property.name} – ${city}` : property.name;
    const title = this.resolveTitle(baseTitle, property.name);

    const description = this.resolveDescription(property, city);

    const image = this.resolveImage(property.images);
    const slug = property.slug ?? String(property.id);
    const url = this.propertyUrl(slug);

    this.update({
      title,
      description,
      image,
      url,
      type: 'website',
    });

    this.setCanonical(url);
    this.emitStructuredData(property);
  }

  /**
   * Build a JSON-LD structured-data object for a property and inject it into
   * <head> as a single <script type="application/ld+json">. Optional nodes are
   * gated behind data presence; missing fields never throw.
   */
  emitStructuredData(property: ObjectDetailDto): void {
    const json: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': this.schemaType(property),
      name: property.name,
    };

    const description = this.plainDescription(property.description);
    if (description) {
      json['description'] = description;
    }

    const images = this.galleryUrls(property.images);
    if (images.length) {
      json['image'] = images;
    }

    const address = this.postalAddress(property.address);
    if (address) {
      json['address'] = address;
    }

    const geo = this.geoCoordinates(property.latitude, property.longitude);
    if (geo) {
      json['geo'] = geo;
    }

    if (property.phone) {
      json['telephone'] = property.phone;
    }
    if (property.email) {
      json['email'] = property.email;
    }
    if (property.website) {
      json['url'] = property.website;
    }

    const offer = this.priceOffer(property.displayPrice);
    if (offer) {
      json['makesOffer'] = offer;
    }

    const aggregateRating = this.aggregateRating(property.reviewSummary);
    if (aggregateRating) {
      json['aggregateRating'] = aggregateRating;
    }

    const reviews = this.reviewNodes(property.reviews);
    if (reviews.length) {
      json['review'] = reviews;
    }

    this.setJsonLd(json);
  }

  /** Whether the property reads more like a single rental than a business. */
  private schemaType(property: ObjectDetailDto): 'LodgingBusiness' | 'VacationRental' {
    const units = property.objectTypes?.length ?? 0;
    return units <= 1 ? 'VacationRental' : 'LodgingBusiness';
  }

  private resolveTitle(baseTitle: string, name: string): string {
    if (!this.branding.isWhiteLabel()) {
      return baseTitle;
    }
    const template = this.branding.metaTitle();
    if (!template) {
      return baseTitle;
    }
    return TITLE_PLACEHOLDER.test(template)
      ? template.replace(TITLE_PLACEHOLDER, name)
      : template;
  }

  private resolveDescription(property: ObjectDetailDto, city: string | null): string {
    if (this.branding.isWhiteLabel()) {
      const branded = this.branding.metaDescription();
      if (branded) {
        return branded;
      }
    }

    const descParts: string[] = [];
    if (city) descParts.push(city);
    if (property.displayPrice) {
      descParts.push(`od ${(property.displayPrice / 100).toFixed(0)} zł/noc`);
    }
    const clean = this.plainDescription(property.description);
    if (clean) {
      const maxLen = 200 - descParts.join(' | ').length - 5;
      descParts.push(clean.length > maxLen ? clean.substring(0, maxLen) + '…' : clean);
    }
    return descParts.join(' | ') || DEFAULT_DESCRIPTION;
  }

  private resolveImage(
    images: { url: string; variants?: { large?: string; medium?: string } }[] | null,
  ): string {
    if (this.branding.isWhiteLabel()) {
      const branded = this.branding.ogImageUrl();
      if (branded) {
        return branded;
      }
    }
    return this.getBestImage(images);
  }

  /**
   * Build the canonical / og:url for a property. On the main domain (and the
   * default `zaklepuj` canonical mode on a white-label host) this points at
   * zaklepuj.pl so subdomains don't create duplicate content. When a white-label
   * host opts into `self`, the canonical is its own origin + current path.
   */
  private propertyUrl(slug: string): string {
    if (this.branding.isWhiteLabel() && this.branding.canonicalMode() === 'self') {
      const origin = this.currentOrigin();
      if (origin) {
        const path = this.currentPath() ?? `/property/${slug}`;
        return `${origin}${path}`;
      }
    }
    return `${CANONICAL_BASE}/property/${slug}`;
  }

  private currentOrigin(): string | null {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }
    return window.location.origin || null;
  }

  private currentPath(): string | null {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }
    return (window.location.pathname || '') + (window.location.search || '');
  }

  private plainDescription(description: string | null | undefined): string | null {
    if (!description) {
      return null;
    }
    const clean = description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return clean || null;
  }

  private galleryUrls(
    images: { url: string; variants?: { large?: string; medium?: string } }[] | null,
  ): string[] {
    if (!images?.length) {
      return [];
    }
    return images.map((img) => img.variants?.large ?? img.variants?.medium ?? img.url);
  }

  private postalAddress(
    address: { city: string | null; street?: string | null; postalCode?: string | null; country?: string | null } | null,
  ): Record<string, unknown> | null {
    if (!address) {
      return null;
    }
    const node: Record<string, unknown> = { '@type': 'PostalAddress' };
    if (address.street) node['streetAddress'] = address.street;
    if (address.city) node['addressLocality'] = address.city;
    if (address.postalCode) node['postalCode'] = address.postalCode;
    if (address.country) node['addressCountry'] = address.country;
    // Only emit when there is at least one real address field.
    return Object.keys(node).length > 1 ? node : null;
  }

  private geoCoordinates(
    latitude: number | null,
    longitude: number | null,
  ): Record<string, unknown> | null {
    if (latitude == null || longitude == null) {
      return null;
    }
    return {
      '@type': 'GeoCoordinates',
      latitude,
      longitude,
    };
  }

  private priceOffer(displayPrice: number | null): Record<string, unknown> | null {
    if (displayPrice == null) {
      return null;
    }
    return {
      '@type': 'Offer',
      price: (displayPrice / 100).toFixed(2),
      priceCurrency: 'PLN',
    };
  }

  /**
   * Build an AggregateRating node from the review summary. Gated behind a
   * positive review count; never throws on malformed/empty data.
   */
  private aggregateRating(
    summary: { count: number; average: number } | null | undefined,
  ): Record<string, unknown> | null {
    if (!summary || !(summary.count > 0)) {
      return null;
    }
    const average = Number(summary.average);
    if (!Number.isFinite(average)) {
      return null;
    }
    return {
      '@type': 'AggregateRating',
      ratingValue: average.toFixed(1),
      reviewCount: summary.count,
      bestRating: 5,
      worstRating: 1,
    };
  }

  /**
   * Build up to MAX_REVIEW_NODES individual Review nodes from the reviews
   * array. Reviews without a usable rating or body are skipped; never throws.
   */
  private reviewNodes(reviews: ReviewDto[] | null | undefined): Record<string, unknown>[] {
    if (!reviews?.length) {
      return [];
    }
    const nodes: Record<string, unknown>[] = [];
    for (const review of reviews) {
      if (nodes.length >= MAX_REVIEW_NODES) {
        break;
      }
      const node = this.reviewNode(review);
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  private reviewNode(review: ReviewDto): Record<string, unknown> | null {
    const rating = Number(review.rating);
    if (!Number.isFinite(rating)) {
      return null;
    }
    const body = (review.text ?? '').trim();
    const author = (review.author ?? '').trim();
    const node: Record<string, unknown> = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: rating,
        bestRating: 5,
        worstRating: 1,
      },
    };
    if (author) {
      node['author'] = { '@type': 'Person', name: author };
    }
    if (body) {
      node['reviewBody'] = body;
    }
    const datePublished = review.stayedAt ?? review.createdAt;
    if (datePublished) {
      node['datePublished'] = datePublished;
    }
    return node;
  }

  private getBestImage(
    images: { url: string; variants?: { large?: string; medium?: string } }[] | null,
  ): string {
    if (!images?.length) return DEFAULT_IMAGE;
    const first = images[0];
    return first.variants?.large ?? first.variants?.medium ?? first.url;
  }

  /** Create or update the single JSON-LD <script> in <head>. */
  private setJsonLd(data: Record<string, unknown>): void {
    const head = this.doc.head;
    if (!head) {
      return;
    }
    let script = head.querySelector<HTMLScriptElement>(
      'script[type="application/ld+json"]',
    );
    if (!script) {
      script = this.doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  private setTag(name: string, content: string): void {
    const isOg = name.startsWith('og:');
    const isTwitter = name.startsWith('twitter:');

    if (isOg) {
      this.meta.updateTag({ property: name, content });
    } else if (isTwitter) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.updateTag({ name, content });
    }
  }
}
